import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { Pager } from '@/components/Pager';
import type { RunHistoryEntry, SubscriptionRunStatus } from '@/types';
import { HelpLink } from '@/components/HelpLink';

const pageSize = 50;

/**
 * How often the FIRST page re-reads itself, and why no other page does.
 *
 * This used to poll every 10 seconds, unconditionally, for 200 rows. That is six reads a minute
 * per open tab of the heaviest query the portal issues — 19,937 execution-log rows, a window
 * COUNT over the same predicate, and a second round trip against dbo.Subscriptions whenever any
 * row on the page failed — which is a large part of why this page was slowing the system down.
 *
 * Polling a deep page is worse than expensive, it is wrong. Rows are ordered newest first and new
 * executions land at the top, so every run that completes shifts the whole offset window down by
 * one: a reader sitting on skip=300 gets rows sliding between pages under them, sees a row twice
 * or misses one entirely, and has no way to tell. Offset paging is only stable while the front of
 * the list is not moving. So the poll is scoped to skip === 0, where new rows appear exactly where
 * the user is already looking and nothing slides out from under them but the oldest row on screen.
 *
 * 60s rather than 10s even there: the scheduled runs this watches are minutes apart, so a
 * 10-second interval buys no freshness anyone can perceive and costs six times the reads. The one
 * place a 10-second answer genuinely matters is watching a run you just started by hand, and the
 * subscription detail page already has exactly that, scoped to a window that closes.
 */
const FIRST_PAGE_POLL_MS = 60_000;

export function RunHistory() {
  const api = useApi();
  const [params] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<SubscriptionRunStatus | ''>('');
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Narrowing lives in the query string rather than component state so the subscription page's
  // "see the rest" link works, and so a narrowed view can be pasted to a colleague.
  //
  // reportPath is the one that narrows to anything. subscriptionId is still read — a link may
  // exist, and the dev-mode reader does populate it — but on the live server no execution-log row
  // carries a subscription id at all, so it filters to nothing; the banner below says as much
  // rather than presenting an empty page as "this never ran".
  const subscriptionId = params.get('subscriptionId') ?? '';
  const reportPath = params.get('reportPath') ?? '';

  // Any change of filter invalidates the offset. Without this, switching from All statuses to
  // Failed while on page 5 asks for rows 200-249 of a set that has 47, and the table comes back
  // empty — which reads as "nothing ever failed" rather than "you are past the end".
  useEffect(() => setSkip(0), [statusFilter, subscriptionId, reportPath]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['runs', statusFilter, subscriptionId, reportPath, skip],
    queryFn: () => api.runHistoryPage({
      status: statusFilter || undefined,
      subscriptionId: subscriptionId || undefined,
      reportPath: reportPath || undefined,
      skip,
      take: pageSize
    }),
    // Without this the table empties on every page step and the whole layout jumps.
    placeholderData: keepPreviousData,
    refetchInterval: skip === 0 ? FIRST_PAGE_POLL_MS : false
  });

  const runs = data?.entries ?? [];
  const total = data?.total ?? 0;

  // Landed past the end: step back to the start rather than stranding the reader.
  //
  // The total is read off the first returned row, so a page with no rows reports a total of zero,
  // and a zero total hides the pager — leaving somebody on page 5 of a set that has shrunk with an
  // empty table and no Previous button to escape with. The filter reset above covers the common
  // way in; this covers the rest, which is mostly the 30-day window moving on beneath a page that
  // was opened before it did.
  //
  // Guarded on `data` rather than `runs.length` alone so it fires on a real answer and not on the
  // undefined first render, and on `isFetching` so it does not race a page that is still arriving.
  useEffect(() => {
    if (data && !isFetching && runs.length === 0 && skip > 0) setSkip(0);
  }, [data, isFetching, runs.length, skip]);

  if (error) return <div className="error-banner">{(error as Error).message}</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Run History</h1>
          <div className="sub">Last 30 days of report executions. Select a row to see the parameters it ran with. <HelpLink slug="check-a-report-was-sent" /></div>
        </div>
      </div>

      {/* Narrowed views say so and offer the way out. Without this the page looks like the whole
          estate with most of it mysteriously missing — the same complaint the status chips on My
          Subscriptions exist to answer. */}
      {reportPath && (
        <div className="notice" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            Showing runs of <strong>{reportPath}</strong> — every subscription on it, and anyone who
            opened it themselves. The execution log does not record which subscription started a run,
            so it cannot be narrowed further.
          </div>
          <Link className="btn-secondary btn-sm" to="/history">Show all runs</Link>
        </div>
      )}

      {subscriptionId && !reportPath && (
        <div className="notice notice-warn" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            Filtering by <strong>one subscription</strong>, which this report server cannot answer:
            its execution log records the report a run belongs to but not the subscription, so this
            returns nothing whether or not the subscription has been running.{' '}
            <Link to={`/subscriptions/${subscriptionId}`}>Open the subscription</Link> to see its
            report's runs instead.
          </div>
          <Link className="btn-secondary btn-sm" to="/history">Show all runs</Link>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <select className="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value as SubscriptionRunStatus | '')}>
          <option value="">All statuses</option>
          <option value="Success">Success</option>
          <option value="Failed">Failed</option>
        </select>
      </div>

      <div className="card">
        {isLoading ? <div className="placeholder">Loading...</div> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Report</th>
                <th>Who</th>
                <th>Status</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <Fragment key={r.executionId}>
                  <tr onClick={() => setExpanded(expanded === r.executionId ? null : r.executionId)}>
                    <td data-label="When" style={{ whiteSpace: 'nowrap' }}>{new Date(r.startedUtc).toLocaleString()}</td>
                    <td data-label="">
                      <div className="report-name">{r.reportName}</div>
                      <div className="report-path">{r.reportPath}</div>
                      {r.errorMessage && <div style={{ fontSize: '.82rem', color: 'var(--kt-red)', marginTop: 4 }}>{r.errorMessage}</div>}
                    </td>
                    <td data-label="Who"><RunOwner run={r} /></td>
                    <td data-label="Status"><StatusPill status={r.status} /></td>
                    <td data-label="Duration" style={{ whiteSpace: 'nowrap' }}>{(r.durationMs / 1000).toFixed(1)}s</td>
                  </tr>
                  {expanded === r.executionId && (
                    <tr className="run-detail-row">
                      <td colSpan={5}><RunParameters run={r} /></td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan={5} className="placeholder">No runs in the selected window.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* `shown` is the rows actually rendered, not pageSize: the API trims reports the caller may
          not see AFTER paging, so a page of 50 can legitimately render 47 and the range has to say
          47 rather than claim three rows it never showed. */}
      <Pager
        skip={skip}
        take={pageSize}
        shown={runs.length}
        total={total}
        busy={isFetching}
        onChange={setSkip}
      />
    </>
  );
}

/** The constant domain prefix is noise once every name carries it. */
function shortAccount(account: string) {
  return account.replace(/^KEY\\/i, '');
}

/**
 * The report server records the account it executed under, which for a subscription is always its
 * own service account. So the name shown is the *owner* — who is answerable for the run — and the
 * execution account is demoted to a footnote, shown only where it says something the owner doesn't.
 */
function RunOwner({ run }: { run: RunHistoryEntry }) {
  const { ownerUserName, ownerSource, executedByAccount } = run;

  if (!ownerUserName) {
    return (
      <span
        className="run-owner-none"
        title={ownerSource === 'Ambiguous'
          ? 'This report has subscriptions belonging to several people, and the execution log does not record which one fired.'
          : 'No owner could be established for this run.'}
      >
        {ownerSource === 'Ambiguous' ? 'Several owners' : 'Unknown'}
      </span>
    );
  }

  return (
    <>
      <div className="run-owner">{shortAccount(ownerUserName)}</div>
      {ownerSource === 'Report' && (
        <div className="run-owner-note" title="The execution log does not name the subscription, but every subscription on this report has the same owner.">
          subscription owner
        </div>
      )}
      {executedByAccount && executedByAccount !== ownerUserName && (
        <div className="run-owner-note" title="The account the report server executed the report under.">
          ran as {executedByAccount}
        </div>
      )}
    </>
  );
}

function RunParameters({ run }: { run: RunHistoryEntry }) {
  if (run.parameters.length === 0) {
    return <div className="run-detail-empty">This run recorded no report parameters.</div>;
  }

  return (
    <div className="run-detail">
      <div className="run-detail-title">Parameters</div>
      <dl className="run-params">
        {run.parameters.map(p => (
          <Fragment key={p.name}>
            <dt>{p.name}</dt>
            <dd>
              {/* Indexed because a parameter can legitimately be given the same value twice. */}
              {p.values.map((v, i) => v === ''
                ? <em key={i} className="run-param-blank">(blank)</em>
                : <span key={i} className="run-param-value">{v}</span>)}
            </dd>
          </Fragment>
        ))}
      </dl>
      {run.parametersTruncated && (
        <div className="run-detail-note">
          This run's parameter list was too long to store in full — later parameters are not shown.
        </div>
      )}
    </div>
  );
}
